import React from "react";
import { Link } from "react-router-dom";
import { Bell, Mail, MessageSquare, MapPin } from "lucide-react";
import PushNotificationsCard from "@/components/PushNotificationsCard";

/**
 * Public-facing "Never miss a drop" landing. Works logged-in OR logged-out
 * and is the default target for the splash Event Promo CTA.
 */
export default function NotifyPage() {
  return (
    <div className="py-10 space-y-8" data-testid="notify-page">
      <header className="max-w-3xl">
        <div className="overline">Stay in the loop</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight mt-2">
          Never <span style={{ color: "#76B82A" }}>miss</span> a drop.
        </h1>
        <p className="mt-4 text-lg text-[#F5F2E8]/80 max-w-xl">
          The best drops happen fast. Here's how to get pinged the second something goes live in your
          city — even if you're nowhere near the site.
        </p>
      </header>

      <div className="grid md:grid-cols-2 gap-6 items-start">
        <section className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest bg-[#76B82A] text-black">
              Recommended
            </span>
            <span className="font-mono text-[11px] text-[#F5F2E8]/60">
              Free · Real-time · No app install needed
            </span>
          </div>
          <PushNotificationsCard />
        </section>

        <section className="space-y-4">
          <div className="overline">Other ways to stay notified</div>

          <div className="loot-card-flat p-5 space-y-2">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-[#D7A62A]" />
              <div className="font-display uppercase text-lg">In-app bell</div>
            </div>
            <p className="text-sm text-[#F5F2E8]/75">
              Every drop, clue, and winner announcement lives in the 🔔 bell on your dashboard when you're
              signed in. Perfect for when you're already on the site.
            </p>
          </div>

          <div className="loot-card-flat p-5 space-y-2">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-[#76B82A]" />
              <div className="font-display uppercase text-lg">City updates</div>
            </div>
            <p className="text-sm text-[#F5F2E8]/75">
              Pin a city on the{" "}
              <Link to="/vote" className="text-[#76B82A] underline">pitch your city page</Link>{" "}
              and we'll notify you the moment a hunt lands there. Great for folks who only care about
              their home turf.
            </p>
          </div>

          <div className="loot-card-flat p-5 space-y-2">
            <div className="flex items-center gap-2">
              <Mail size={16} className="text-[#F5F2E8]/70" />
              <div className="font-display uppercase text-lg">Email digest</div>
            </div>
            <p className="text-sm text-[#F5F2E8]/75">
              <Link to="/register" className="text-[#76B82A] underline">Sign up</Link>{" "}
              and we'll send a weekly "what went down" with winners, upcoming cities, and community wins.
              Low volume, no spam.
            </p>
          </div>

          <div className="loot-card-flat p-5 space-y-2">
            <div className="flex items-center gap-2">
              <MessageSquare size={16} className="text-[#E53935]" />
              <div className="font-display uppercase text-lg">SMS alerts (coming soon)</div>
            </div>
            <p className="text-sm text-[#F5F2E8]/75">
              Text-message pings for drops &amp; Golden Drops are on the roadmap. Opt-in only — we'll never
              text you without explicit consent.
            </p>
          </div>
        </section>
      </div>

      <div className="max-w-3xl text-sm text-[#F5F2E8]/55 font-mono border-t border-[#F5F2E8]/10 pt-5">
        We will only ping you for real moments — drops going live, clues unlocking, new hunts in your
        city, winners announced. No ads. Turn it off anytime with one tap.
      </div>
    </div>
  );
}
